/**
 * ComplianceConfigurationDashboard Component
 *
 * Regulatory compliance threshold configuration interface (HIPAA §164.514, FDA guidance)
 *
 * Features:
 * - Multi-framework support (HIPAA, FDA, GDPR, SOX)
 * - Real-time threshold editing with bias detection
 * - Emergency override request workflow
 * - Bias score monitoring and alerting
 * - Complete audit trail visualization
 * - Multi-jurisdiction compliance
 *
 * Compliance Requirements:
 * - HIPAA §164.514: De-identification threshold configuration
 * - FDA: AI bias detection in clinical decision workflows
 * - GDPR: Data minimization threshold enforcement
 * - SOX: Financial control threshold management
 *
 * @module ComplianceConfigurationDashboard
 */

import React, { useState, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  Title,
  Text,
  Badge,
  Group,
  Stack,
  Tabs,
  Alert,
  Loader,
  Center,
  ScrollArea,
  ActionIcon,
  Tooltip,
  Progress,
  RingProgress,
  ThemeIcon,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconAlertTriangle,
  IconSettings,
  IconHistory,
  IconReportAnalytics,
  IconAlertCircle, // Use IconAlertCircle for emergency alerts
  IconRefresh,
} from '@tabler/icons-react';
import { useRegulatoryCompliance } from '../hooks/useRegulatoryCompliance';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface ComplianceConfigurationDashboardProps {
  /**
   * Tenant ID (from useCurrentTenant hook)
   */
  tenant: string;

  /**
   * Default framework to display ("HIPAA", "FDA", "GDPR", "SOX")
   */
  defaultFramework?: string;

  /**
   * Enable emergency override panel
   */
  allowEmergencyOverrides?: boolean;

  /**
   * Bias score alert threshold (0-1, default 0.7 per FDA)
   */
  biasAlertThreshold?: number;

  /**
   * Domain for compliance configuration (healthcare, financial, legal, etc.)
   * Used for domain-specific compliance rules
   */
  domain?: string;

  /**
   * Enable auto-refresh of compliance data
   */
  autoRefresh?: boolean;

  /**
   * Refresh interval in seconds (default: 60)
   */
  refreshIntervalSeconds?: number;
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * ComplianceConfigurationDashboard Component
 *
 * Main dashboard for regulatory compliance threshold configuration
 *
 * @param props - Component props
 * @returns JSX.Element
 *
 * @example
 * ```tsx
 * <ComplianceConfigurationDashboard
 *   tenant="tenant-123"
 *   defaultFramework="HIPAA"
 *   allowEmergencyOverrides={true}
 *   biasAlertThreshold={0.7}
 * />
 * ```
 */
export function ComplianceConfigurationDashboard({
  tenant,
  defaultFramework = 'HIPAA',
  allowEmergencyOverrides = true,
  biasAlertThreshold = 0.7,
}: ComplianceConfigurationDashboardProps): JSX.Element {
  // ============================================================================
  // State Management
  // ============================================================================

  const [selectedFramework, setSelectedFramework] = useState<string>(defaultFramework);
  const [activeTab, setActiveTab] = useState<string>('thresholds');

  // ============================================================================
  // Hooks
  // ============================================================================

  const {
    thresholds,
    frameworks,
    biasScores,
    activeOverrides,
    loading,
    subscriptionActive,
    error,
    updateThreshold,
    requestEmergencyOverride,
    filterByFramework,
    getHighBiasThresholds,
    calculateBiasScore,
  } = useRegulatoryCompliance(tenant, selectedFramework);

  // ============================================================================
  // Computed Values
  // ============================================================================

  /**
   * Filtered thresholds for selected framework
   */
  const filteredThresholds = useMemo(() => {
    return filterByFramework(selectedFramework);
  }, [filterByFramework, selectedFramework]);

  /**
   * High bias thresholds requiring attention
   */
  const highBiasThresholds = useMemo(() => {
    return getHighBiasThresholds(biasAlertThreshold);
  }, [getHighBiasThresholds, biasAlertThreshold]);

  /**
   * Overall compliance score (0-100)
   */
  const complianceScore = useMemo(() => {
    if (filteredThresholds.length === 0) return 100;

    const compliantCount = filteredThresholds.filter((t) => {
      const biasScore = calculateBiasScore(t.id);
      return biasScore === null || biasScore < biasAlertThreshold;
    }).length;

    return Math.round((compliantCount / filteredThresholds.length) * 100);
  }, [filteredThresholds, calculateBiasScore, biasAlertThreshold]);

  /**
   * Compliance status color
   */
  const complianceColor = useMemo(() => {
    if (complianceScore >= 90) return 'green';
    if (complianceScore >= 70) return 'yellow';
    return 'red';
  }, [complianceScore]);

  // ============================================================================
  // Error Handling
  // ============================================================================

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Error Loading Compliance Configuration"
          color="red"
          variant="filled"
        >
          {error.message}
        </Alert>
      </Container>
    );
  }

  // ============================================================================
  // Loading State
  // ============================================================================

  if (loading && thresholds.length === 0) {
    return (
      <Container size="xl" py="xl">
        <Center style={{ minHeight: '400px' }}>
          <Stack ta="center" gap="md">
            <Loader size="xl" />
            <Text size="lg" c="dimmed">
              Loading compliance configuration...
            </Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* ================================================================== */}
        {/* Header Section */}
        {/* ================================================================== */}

        <Group justify="space-between" ta="left">
          <div>
            <Title order={1}>Regulatory Compliance Configuration</Title>
            <Text size="sm" c="dimmed" mt="xs">
              Configure thresholds for HIPAA §164.514, FDA, GDPR, and SOX compliance
            </Text>
          </div>

          <Group gap="xs">
            {/* Subscription status indicator */}
            <Tooltip
              label={
                subscriptionActive
                  ? 'Real-time updates active'
                  : 'Real-time updates inactive'
              }
            >
              <ThemeIcon
                color={subscriptionActive ? 'green' : 'gray'}
                variant="light"
                size="lg"
              >
                <IconRefresh size={16} />
              </ThemeIcon>
            </Tooltip>

            {/* Compliance score ring */}
            <Tooltip label={`Compliance Score: ${complianceScore}%`}>
              <RingProgress
                size={60}
                thickness={6}
                sections={[{ value: complianceScore, color: complianceColor }]}
                label={
                  <Center>
                    <Text size="xs" fw={700}>
                      {complianceScore}%
                    </Text>
                  </Center>
                }
              />
            </Tooltip>
          </Group>
        </Group>

        {/* ================================================================== */}
        {/* High Bias Alert */}
        {/* ================================================================== */}

        {highBiasThresholds.length > 0 && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="High Bias Detected in AI Workflows"
            color="orange"
            variant="light"
          >
            <Text size="sm">
              {highBiasThresholds.length} threshold(s) have bias scores ≥{' '}
              {biasAlertThreshold}. FDA guidance requires review and mitigation.
            </Text>
            <Group gap="xs" mt="sm">
              {highBiasThresholds.map((t) => (
                <Badge key={t.id} color="orange" variant="light">
                  {t.thresholdType} ({calculateBiasScore(t.id)?.toFixed(2)})
                </Badge>
              ))}
            </Group>
          </Alert>
        )}

        {/* ================================================================== */}
        {/* Active Emergency Overrides Alert */}
        {/* ================================================================== */}

        {activeOverrides.length > 0 && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Active Emergency Overrides"
            color="red"
            variant="light"
          >
            <Text size="sm">
              {activeOverrides.length} emergency override(s) currently active. Review
              expiration times.
            </Text>
          </Alert>
        )}

        {/* ================================================================== */}
        {/* Framework Selector */}
        {/* ================================================================== */}

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group gap="md">
            <IconSettings size={20} />
            <Text fw={500}>Select Compliance Framework:</Text>
            <Group gap="xs">
              {frameworks.map((framework) => (
                <Badge
                  key={framework.frameworkId}
                  size="lg"
                  variant={selectedFramework === framework.frameworkId ? 'filled' : 'light'}
                  color={selectedFramework === framework.frameworkId ? 'blue' : 'gray'}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedFramework(framework.frameworkId)}
                >
                  {framework.name}
                </Badge>
              ))}
            </Group>
          </Group>
        </Card>

        {/* ================================================================== */}
        {/* Tabbed Interface */}
        {/* ================================================================== */}

        <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'thresholds')}>
          <Tabs.List>
            <Tabs.Tab value="thresholds" leftSection={<IconShieldCheck size={16} />}>
              Thresholds ({filteredThresholds.length})
            </Tabs.Tab>
            <Tabs.Tab value="bias" leftSection={<IconReportAnalytics size={16} />}>
              Bias Monitoring ({biasScores.length})
            </Tabs.Tab>
            {allowEmergencyOverrides && (
              <Tabs.Tab value="overrides" leftSection={<IconAlertCircle size={16} />}>
                Emergency Overrides ({activeOverrides.length})
              </Tabs.Tab>
            )}
            <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
              Audit Trail
            </Tabs.Tab>
          </Tabs.List>

          {/* ============================================================== */}
          {/* Thresholds Tab */}
          {/* ============================================================== */}

          <Tabs.Panel value="thresholds" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between">
                  <Title order={3}>
                    {selectedFramework} Regulatory Thresholds
                  </Title>
                  <Badge color="blue" variant="light">
                    {filteredThresholds.length} Active
                  </Badge>
                </Group>

                <ScrollArea h={500}>
                  <Stack gap="md">
                    {filteredThresholds.length === 0 ? (
                      <Center style={{ minHeight: '200px' }}>
                        <Text size="sm" c="dimmed">
                          No thresholds configured for {selectedFramework}
                        </Text>
                      </Center>
                    ) : (
                      filteredThresholds.map((threshold) => (
                        <ThresholdCard
                          key={threshold.id}
                          threshold={threshold}
                          biasScore={calculateBiasScore(threshold.id)}
                          biasAlertThreshold={biasAlertThreshold}
                          onUpdate={updateThreshold}
                        />
                      ))
                    )}
                  </Stack>
                </ScrollArea>
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* ============================================================== */}
          {/* Bias Monitoring Tab */}
          {/* ============================================================== */}

          <Tabs.Panel value="bias" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Title order={3}>AI Bias Score Monitoring (FDA Compliance)</Title>

                <ScrollArea h={500}>
                  <Stack gap="md">
                    {biasScores.length === 0 ? (
                      <Center style={{ minHeight: '200px' }}>
                        <Text size="sm" c="dimmed">
                          No bias scores available
                        </Text>
                      </Center>
                    ) : (
                      biasScores.map((metrics) => (
                        <BiasScoreCard
                          key={metrics.thresholdId}
                          metrics={metrics}
                          threshold={
                            thresholds.find((t) => t.id === metrics.thresholdId)!
                          }
                          alertThreshold={biasAlertThreshold}
                        />
                      ))
                    )}
                  </Stack>
                </ScrollArea>
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* ============================================================== */}
          {/* Emergency Overrides Tab */}
          {/* ============================================================== */}

          {allowEmergencyOverrides && (
            <Tabs.Panel value="overrides" pt="md">
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Title order={3}>Emergency Override Requests</Title>

                  <ScrollArea h={500}>
                    <Stack gap="md">
                      {activeOverrides.length === 0 ? (
                        <Center style={{ minHeight: '200px' }}>
                          <Text size="sm" c="dimmed">
                            No active emergency overrides
                          </Text>
                        </Center>
                      ) : (
                        activeOverrides.map((override) => (
                          <EmergencyOverrideCard
                            key={override.id}
                            override={override}
                            threshold={
                              thresholds.find((t) => t.id === override.thresholdId)!
                            }
                          />
                        ))
                      )}
                    </Stack>
                  </ScrollArea>
                </Stack>
              </Card>
            </Tabs.Panel>
          )}

          {/* ============================================================== */}
          {/* Audit Trail Tab */}
          {/* ============================================================== */}

          <Tabs.Panel value="history" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Title order={3}>Threshold Change Audit Trail</Title>

                <Center style={{ minHeight: '200px' }}>
                  <Text size="sm" c="dimmed">
                    Audit trail implementation pending
                  </Text>
                </Center>
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * Threshold Card Component
 */
interface ThresholdCardProps {
  threshold: any;
  biasScore: number | null;
  biasAlertThreshold: number;
  onUpdate: (input: any) => Promise<boolean>;
}

function ThresholdCard({
  threshold,
  biasScore,
  biasAlertThreshold,
  onUpdate,
}: ThresholdCardProps): JSX.Element {
  const hasBiasIssue = biasScore !== null && biasScore >= biasAlertThreshold;

  return (
    <Card
      shadow="xs"
      padding="md"
      radius="sm"
      withBorder
      style={{
        borderLeft: hasBiasIssue ? '4px solid orange' : undefined,
      }}
    >
      <Group justify="space-between" ta="left">
        <Stack gap="xs" style={{ flex: 1 }}>
          <Group gap="xs">
            <Text fw={600} size="sm">
              {threshold.thresholdType.replace(/_/g, ' ').toUpperCase()}
            </Text>
            {hasBiasIssue && (
              <Badge color="orange" size="sm" variant="light">
                High Bias
              </Badge>
            )}
          </Group>

          <Group gap="md">
            <Text size="sm" c="dimmed">
              Value:
            </Text>
            <Text size="sm" fw={500}>
              {threshold.numericValue} {threshold.unit}
            </Text>
          </Group>

          {threshold.sourceAuthority && (
            <Text size="xs" c="dimmed">
              Authority: {threshold.sourceAuthority}
            </Text>
          )}

          {biasScore !== null && (
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Bias Score:
              </Text>
              <Badge color={hasBiasIssue ? 'orange' : 'green'} size="xs">
                {biasScore.toFixed(2)}
              </Badge>
            </Group>
          )}
        </Stack>

        <Group gap="xs">
          <Tooltip label="Edit threshold">
            <ActionIcon variant="light" color="blue" size="lg">
              <IconSettings size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="View history">
            <ActionIcon variant="light" color="gray" size="lg">
              <IconHistory size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Card>
  );
}

/**
 * Bias Score Card Component
 */
interface BiasScoreCardProps {
  metrics: any;
  threshold: any;
  alertThreshold: number;
}

function BiasScoreCard({
  metrics,
  threshold,
  alertThreshold,
}: BiasScoreCardProps): JSX.Element {
  const isHighBias = metrics.currentScore >= alertThreshold;

  return (
    <Card shadow="xs" padding="md" radius="sm" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600} size="sm">
            {threshold.thresholdType.replace(/_/g, ' ').toUpperCase()}
          </Text>
          <Badge color={isHighBias ? 'orange' : 'green'} variant="light">
            {metrics.currentScore.toFixed(2)}
          </Badge>
        </Group>

        <Progress
          value={metrics.currentScore * 100}
          color={isHighBias ? 'orange' : 'green'}
          size="lg"
        />

        <Group gap="md">
          <Text size="xs" c="dimmed">
            Trend: {metrics.trend}
          </Text>
          <Text size="xs" c="dimmed">
            Last Calculated: {new Date(metrics.lastCalculated).toLocaleString()}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Emergency Override Card Component
 */
interface EmergencyOverrideCardProps {
  override: any;
  threshold: any;
}

function EmergencyOverrideCard({
  override,
  threshold,
}: EmergencyOverrideCardProps): JSX.Element {
  return (
    <Card shadow="xs" padding="md" radius="sm" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600} size="sm">
            {threshold.thresholdType.replace(/_/g, ' ').toUpperCase()}
          </Text>
          <Badge color={override.status === 'active' ? 'red' : 'gray'} variant="light">
            {override.status.toUpperCase()}
          </Badge>
        </Group>

        <Group gap="md">
          <Text size="sm" c="dimmed">
            Override Value:
          </Text>
          <Text size="sm" fw={500}>
            {override.overrideValue}
          </Text>
        </Group>

        <Text size="xs" c="dimmed">
          Justification: {override.justification}
        </Text>

        <Group gap="md">
          <Text size="xs" c="dimmed">
            Expires: {new Date(override.expiresAt).toLocaleString()}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}

export default ComplianceConfigurationDashboard;
